import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const reviews = [
  {
    name: 'Martin Kováč',
    company: 'CEO, TechFlow s.r.o.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20slovak%20businessman%20portrait%20photo%20headshot%20confident%20smile%20dark%20hair%20clean%20white%20background%20corporate%20style%20photography%20high%20quality%20sharp%20focus&width=200&height=200&seq=tw-av1&orientation=squarish',
    rating: 5,
    text: 'Boostio nám vytvorilo web, ktorý skutočne predáva. Organická návštevnosť vzrástla o 280% za 3 mesiace. Tím rozumie biznisu, nie len kódu.',
    metric: '+280% návštevnosť',
    metricIcon: 'ri-arrow-up-line',
  },
  {
    name: 'Zuzana Pálková',
    company: 'Majiteľka, KráľovskáSpa',
    avatar: 'https://readdy.ai/api/search-image?query=elegant%20professional%20slovak%20woman%20portrait%20headshot%20warm%20smile%20dark%20hair%20clean%20bright%20background%20professional%20photography%20high%20quality&width=200&height=200&seq=tw-av2&orientation=squarish',
    rating: 5,
    text: 'Krásny web s rezervačným systémom. Zákazníci chvália jednoduchosť a my máme konečne poriadok v rezerváciách. Odovzdali do 10 dní, presne ako sľúbili.',
    metric: '+65% online rezervácií',
    metricIcon: 'ri-calendar-check-line',
  },
  {
    name: 'Peter Sloboda',
    company: 'Majiteľ, AutoServis Premium',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20middle-aged%20slovak%20man%20portrait%20photo%20headshot%20friendly%20smile%20gray%20hair%20clean%20white%20background%20corporate%20photography%20high%20quality%20sharp%20focus&width=200&height=200&seq=tw-av3&orientation=squarish',
    rating: 5,
    text: 'Mal som starý web z roku 2015. Boostio ho kompletne pretransformovalo. Servisná linka sa zaplnila na mesiace dopredu. Výborná komunikácia počas celého projektu.',
    metric: '+190% nových zákazníkov',
    metricIcon: 'ri-user-add-line',
  },
  {
    name: 'Lucia Benková',
    company: 'Zakladateľka, NutriLife',
    avatar: 'https://readdy.ai/api/search-image?query=young%20professional%20slovak%20woman%20portrait%20photo%20headshot%20bright%20smile%20blonde%20hair%20clean%20light%20background%20modern%20corporate%20photography%20high%20quality&width=200&height=200&seq=tw-av4&orientation=squarish',
    rating: 5,
    text: 'Profesionálny prístup od prvého hovoru. Web dostali sme presne taký, aký sme si predstavovali — a ešte lepší. Teraz ho viem aktualizovať sama bez pomoci.',
    metric: '4.9★ hodnotenie webu',
    metricIcon: 'ri-star-line',
  },
];

export default function TWTestimonials() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-50">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-star-fill text-amber-400 text-sm" />
              <span className="text-zinc-700 text-sm font-semibold">Čo hovoria klienti</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              47 spokojných klientov nemôže mýliť
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed">
              Reálne hodnotenia od reálnych ľudí. Žiadne vymyslené príbehy.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {reviews.map((review, i) => (
              <div
                key={review.name}
                className={`bg-white border border-zinc-100 rounded-2xl p-7 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: review.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-amber-400 text-sm" />
                  ))}
                </div>

                <p className="text-zinc-700 text-sm leading-relaxed mb-6 italic">
                  &ldquo;{review.text}&rdquo;
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-zinc-900 font-bold text-sm">{review.name}</p>
                      <p className="text-zinc-400 text-xs">{review.company}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">
                    <i className={`${review.metricIcon} text-emerald-500 text-xs`} />
                    <span className="text-emerald-600 text-xs font-bold whitespace-nowrap">{review.metric}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
